/**
 * TASTENKUERZEL DER WARTESCHLANGE — sicher gegen Tippen (Pruefung 25.10.2026).
 *
 * Auf Rafaels Mac mit deutscher Tastatur erzeugt Option+L ein „@“ und Option+E ein „€“, Option+←/→ springt im Textfeld wortweise. Deshalb: (1) Ereignisse aus Eingabeelementen
 * (input, textarea, select, contenteditable, role=textbox/combobox/searchbox) und aus eingebetteten Fenstern (iframe, webview) werden IMMER ignoriert — sowohl `target` als auch das
 * aktive Element; (2) nur Kombinationen mit STRG+ALT und einer Pfeiltaste (Tasten ohne Zeichen); (3) Tastenwiederholung zaehlt nicht; (4) fuer „Erledigt“ gibt es KEIN Kuerzel.
 * Auch ein aktives Element INNERHALB eines Dialogs/Popups (role=dialog, listbox, menu, tree, grid) zaehlt — die Pfeile gehoeren dort dem Element.
 * Gepruft gegen die vorhandenen Kuerzel der CUI (App.tsx: Cmd+Ziffern/Cmd+Shift …, QuickActions: Cmd/Strg+K, Escape in Dialogen, Electron-Menue: Cmd+Shift+Alt+R) — keine Kollision.
 */
export type QueueTaste = 'weiter' | 'zurueck' | 'spaeter';
export interface TastenZiel { matches?: (selektor: string) => boolean; closest?: (selektor: string) => unknown; isContentEditable?: boolean }
export const QUEUE_KUERZEL: Record<QueueTaste, string> = { weiter: 'Strg+Alt+→', zurueck: 'Strg+Alt+←', spaeter: 'Strg+Alt+↓' };
const EINGABE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"], [role="combobox"], [role="searchbox"], iframe, webview, embed, object';

/** Dialoge und Popups (Auswahllisten, Menues, Baeume, Tabellen): dort gehoeren die Pfeiltasten dem Element, nicht der Warteschlange. */
const POPUP = '[role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"], [role="menubar"], [role="tree"], [role="grid"], dialog';
export function istEingabeZiel(el: TastenZiel | null | undefined): boolean {
  if (!el) return false;
  return el.isContentEditable === true || el.matches?.(EINGABE) === true || !!el.closest?.(POPUP);
}

export interface TastenEreignis { ctrlKey: boolean; altKey: boolean; metaKey: boolean; shiftKey: boolean; repeat: boolean; code: string; target?: unknown }
export function queueTaste(ev: TastenEreignis, aktiv: TastenZiel | null | undefined): QueueTaste | null {
  if (ev.repeat) return null;
  if (!ev.ctrlKey || !ev.altKey || ev.metaKey || ev.shiftKey) return null;
  if (istEingabeZiel(ev.target as TastenZiel | null | undefined) || istEingabeZiel(aktiv)) return null;
  if (ev.code === 'ArrowRight') return 'weiter';
  if (ev.code === 'ArrowLeft') return 'zurueck';
  if (ev.code === 'ArrowDown') return 'spaeter';
  return null;
}

/**
 * Steht fuer diese Buehne noch Ungesendetes im Feld (freier Text, gewaehlte Antwort, Notiz)? Liest die Entwuerfe der Ansicht (`guide-entwurf:<Buehne>:…`) als Text — ohne JSON zu
 * parsen: ein Eintrag mit nichtleerem Textwert genuegt. „Erledigt → Weiter“ fragt dann einmal nach, damit nichts still verloren geht.
 */
export function hatUngesendetes(store: Pick<Storage, 'getItem'>, buehneId: string): boolean {
  return ['freies-wort', 'wahl', 'notiz'].some(art => {
    const roh = store.getItem(`guide-entwurf:${buehneId}:${art}`);
    return !!roh && /":\s*"[^"]/.test(roh);
  });
}
